import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './ChatConversationsJournal.css';

const API = '/api/ai/admin/conversations';

const formatDt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const ChatConversationsJournal = ({ token }) => {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);

  const h = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (status) params.status = status;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(API, { headers: h, params });
      setList(res.data.conversations || []);
      setTotal(res.data.total || 0);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [page, status, search, token]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (sessionId) => {
    if (expanded === sessionId) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(sessionId);
    try {
      const res = await axios.get(`${API}/${sessionId}`, { headers: h });
      setDetail(res.data);
    } catch {
      setDetail(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="chat-journal">
      <h3>💬 Журнал переписок AI-чата</h3>
      <p className="chat-journal-hint">Каждая сессия — отдельный диалог с датой, IP и данными клиента</p>

      <div className="chat-journal-filters">
        <input
          type="search"
          placeholder="Поиск: IP, email, телефон, session ID…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Все статусы</option>
          <option value="active">Активные</option>
          <option value="completed">Завершённые</option>
          <option value="abandoned">Брошенные</option>
        </select>
        <button type="button" className="btn btn-outline btn-sm" onClick={load}>Обновить</button>
      </div>

      {loading ? (
        <p className="chat-journal-loading">Загрузка…</p>
      ) : list.length === 0 ? (
        <p className="chat-journal-empty">Переписок пока нет</p>
      ) : (
        <div className="chat-journal-list">
          {list.map(conv => {
            const msgCount = conv.messages?.length || 0;
            const userCount = conv.messages?.filter(m => m.role === 'user').length || 0;
            const isOpen = expanded === conv.sessionId;
            return (
              <div key={conv.sessionId} className={`chat-journal-item ${isOpen ? 'open' : ''}`}>
                <button type="button" className="chat-journal-item-head" onClick={() => openDetail(conv.sessionId)}>
                  <div className="chat-journal-item-main">
                    <span className={`chat-journal-status status-${conv.status}`}>{conv.status}</span>
                    <strong>{conv.contactInfo?.name || 'Аноним'}</strong>
                    <span className="chat-journal-meta">{formatDt(conv.updatedAt || conv.createdAt)}</span>
                  </div>
                  <div className="chat-journal-item-sub">
                    <span>IP: {conv.ip || '—'}</span>
                    <span>Сообщений: {msgCount} (клиент: {userCount})</span>
                    {conv.contactInfo?.phone && <span>📞 {conv.contactInfo.phone}</span>}
                    {conv.contactInfo?.email && <span>✉️ {conv.contactInfo.email}</span>}
                  </div>
                </button>

                {isOpen && detail?.sessionId === conv.sessionId && (
                  <div className="chat-journal-detail">
                    <div className="chat-journal-info-grid">
                      <div><b>Session ID:</b> <code>{detail.sessionId}</code></div>
                      <div><b>Создано:</b> {formatDt(detail.createdAt)}</div>
                      <div><b>Обновлено:</b> {formatDt(detail.updatedAt)}</div>
                      <div><b>IP:</b> {detail.ip || '—'}</div>
                      <div className="full-width"><b>User-Agent:</b> {detail.userAgent || '—'}</div>
                      <div><b>Источник:</b> {detail.source || 'website'}</div>
                    </div>
                    <div className="chat-journal-messages">
                      {(detail.messages || []).filter(m => m.role !== 'system').map((m, i) => (
                        <div key={i} className={`chat-journal-msg ${m.role}`}>
                          <div className="chat-journal-msg-head">
                            <span>{m.role === 'user' ? '👤 Клиент' : '🤖 AI'}</span>
                            <time>{formatDt(m.timestamp)}</time>
                          </div>
                          <div className="chat-journal-msg-body">{m.content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="chat-journal-pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span>{page} / {pages} ({total})</span>
          <button type="button" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}
    </div>
  );
};

export default ChatConversationsJournal;
